import * as React from 'react';

import {
  PlayCircleIcon,
  ModalContext,
  ModalModel,
} from '@notino/react-styleguide';
import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import { ModalGallery } from '@components/ModalGallery';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { useTrackingContext } from '@context/tracking/TrackingContext';

import { useNewPdDesignEnabled } from '../hooks/useNewPdDesignEnabled';
import { VirtualTryOnState } from '../model';

import { THUMBS_SLIDER_CONFIG } from './constants';
import { ImageSlider } from './ImageSlider';
import { MainImage } from './MainImg';
import { ModiFaceOverlay } from './ModiFaceOverlay';
import {
  Container,
  MainImgWrapper,
  ImageWrapper,
  VideoWrapper,
  StyledCol,
  PictogramWrapper,
  ThumbsWrapper,
} from './styled';
import { Thumbs } from './Thumbs';
import { ScrollThumbs } from './Thumbs/ScrollThumbs';
import { trackProductImageClick } from './tracking';
import { getImagesAttributes, getVideoAttrs } from './utils';

export interface IProductImageGalleryProps {
  media: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['variants'][number]['media'];
  domain: string;
  modifaceMobileEnabled?: boolean;
  modifaceDesktopEnabled?: boolean;
  openModifaceModal?: () => void;
  isBrandLogoVisible?: boolean;
  pictogram?: JSX.Element | null;
}

export const ProductImageGallery: React.FC<IProductImageGalleryProps> = ({
  domain,
  media,
  openModifaceModal,
  modifaceMobileEnabled = false,
  modifaceDesktopEnabled = false,
  isBrandLogoVisible = false,
  pictogram,
}) => {
  const [imageIndex, setImageIndex] = React.useState(0);
  const newPdDesign = useNewPdDesignEnabled();
  const { toggleModal, hideModal } = ModalContext.useModalContext();

  const { virtualTryOnState } = useFeatureFlags();

  const { getTimeFromInit } = useTrackingContext();

  const { imagesAttrsOrder, imagesAttrsZoom, imagesAttrsOriginal } =
    React.useMemo(() => {
      const imagesArray = [
        media.mainImage,
        media.previewImage,
        ...media.others,
      ].filter((item) => item);

      const [mainImage, ...otherImages] = imagesArray;
      return {
        imagesAttrsOrder: [
          ...getImagesAttributes(imagesArray, domain, 'order'),
          ...media.videos.map((video) => getVideoAttrs(video)),
        ],
        imagesAttrsZoom: getImagesAttributes(imagesArray, domain, 'zoomImage'),
        imagesAttrsOriginal: [
          // let first image be in a little worse quality, so it loads faster for better LCP and UX
          ...getImagesAttributes([mainImage], domain, 'detailMain'),
          ...getImagesAttributes(otherImages, domain, 'detailGallery'),
        ],
      };
    }, [media, domain]);

  const handleMainImageClick = React.useCallback(
    (imgIndex?: number) => (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      trackProductImageClick({
        timing: getTimeFromInit(),
      });

      if (imgIndex) {
        setImageIndex(imgIndex);
      }

      toggleModal(
        <ModalGallery
          onClose={hideModal}
          imageIndex={imgIndex}
          images={imagesAttrsZoom}
          thumbImages={imagesAttrsOrder}
          videos={media.videos}
        />,
        {
          center: true,
          size: ModalModel.Sizes.s1200,
          onlyTopBorder: true,
          noBorders: true,
        }
      );
    },
    [
      getTimeFromInit,
      toggleModal,
      hideModal,
      imagesAttrsOrder,
      imagesAttrsZoom,
      media.videos,
    ]
  );

  const videoIndex = imageIndex - imagesAttrsOriginal.length;
  const isVideo: boolean = videoIndex >= 0;

  const shouldShowModiFaceOverlay =
    modifaceDesktopEnabled &&
    imageIndex === 0 &&
    virtualTryOnState === VirtualTryOnState.Default;

  return (
    <Container
      id="pdImageGallery"
      brandLogo={isBrandLogoVisible}
      newPdDesign={newPdDesign}
    >
      {pictogram ? (
        <PictogramWrapper newPd={newPdDesign}>{pictogram}</PictogramWrapper>
      ) : null}

      <ImageSlider
        imagesAttrs={imagesAttrsOriginal}
        videos={media.videos}
        hasModiface={modifaceMobileEnabled && shouldShowModiFaceOverlay}
        openModifaceModal={openModifaceModal}
      />

      <StyledCol newPdDesign={newPdDesign}>
        <MainImgWrapper
          data-testid="main-image-wrapper"
          newPdDesign={newPdDesign}
          onClick={newPdDesign ? handleMainImageClick(imageIndex) : undefined}
        >
          <ImageWrapper newPdDesign={newPdDesign}>
            {!isVideo && (
              <MainImage
                imageAttrs={imagesAttrsOriginal[imageIndex]}
                onClick={handleMainImageClick(imageIndex)}
                index={imageIndex}
              />
            )}

            {isVideo && (
              <VideoWrapper onClick={handleMainImageClick(imageIndex)}>
                <img
                  src={getVideoAttrs(media.videos[videoIndex], 'mqdefault').src}
                  alt=""
                />

                <PlayCircleIcon
                  width="70px"
                  height="70px"
                  color="icon.primary"
                />
              </VideoWrapper>
            )}

            {shouldShowModiFaceOverlay && (
              <ModiFaceOverlay
                onClick={(e) => {
                  e.stopPropagation();
                  openModifaceModal();
                }}
              />
            )}
          </ImageWrapper>
        </MainImgWrapper>

        {imagesAttrsOrder.length > 1 && !newPdDesign && (
          <ThumbsWrapper>
            <Thumbs
              imagesAttrs={imagesAttrsOrder}
              imageIndex={imageIndex}
              handleImageChange={setImageIndex}
              handleImageClick={handleMainImageClick}
              config={THUMBS_SLIDER_CONFIG}
              cypressDataValue="pd-gallery-slider"
            />
          </ThumbsWrapper>
        )}

        {imagesAttrsOrder.length > 1 && newPdDesign && (
          <ScrollThumbs
            imagesAttrs={imagesAttrsOrder}
            imageIndex={imageIndex}
            handleImageChange={setImageIndex}
            handleImageClick={handleMainImageClick}
            cypressDataValue="pd-gallery-slider"
          />
        )}
      </StyledCol>
    </Container>
  );
};
